import React, { useEffect, useState } from 'react';
import './App.css';

function PrivacyPolicyPage() {
    // Mobile detection
    const [isMobile, setIsMobile] = useState(false);

    // Check for mobile on mount
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return (
        <div style={{
            minHeight: '100vh',
            width: '100vw',
            display: 'flex',
            flexDirection: 'column',
            background: 'none',
            position: 'relative'
        }}>
            {/* Top Bar */}
            <div style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: isMobile ? '24px 24px 0 24px' : '32px 48px 0 48px',
                boxSizing: 'border-box'
            }}>
                {/* Logo */}
                <div style={{
                    fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                    fontWeight: 700,
                    fontSize: isMobile ? '1.5rem' : '2rem',
                    color: '#222',
                    letterSpacing: '-0.02em'
                }}>
                    Carrie.ai
                </div>
                {/* Nav */}
                <div style={{
                    display: 'flex',
                    gap: isMobile ? 16 : 32,
                    fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                    fontWeight: 500,
                    fontSize: isMobile ? '0.9rem' : '1.05rem'
                }}>
                    <a href="/" style={{ color: '#222', textDecoration: 'none', opacity: 0.7 }}>Home</a>
                    <a href="/demo" style={{ color: '#222', textDecoration: 'none', opacity: 0.7 }}>Book a Demo</a>
                </div>
            </div>

            {/* Main Content */}
            <main style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                marginTop: isMobile ? 32 : 64,
                paddingBottom: isMobile ? 40 : 80,
                padding: isMobile ? '0 24px' : '0 48px'
            }}>
                {/* Header */}
                <div style={{
                    width: '100%',
                    maxWidth: 800,
                    marginBottom: isMobile ? 40 : 60
                }}>
                    <h1 style={{
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 700,
                        fontSize: isMobile ? '2rem' : '3rem',
                        color: '#181c2a',
                        textAlign: 'center',
                        margin: '0 0 16px 0',
                        letterSpacing: '-0.02em',
                        lineHeight: 1.2
                    }}>
                        Privacy Policy
                    </h1>
                    <p style={{
                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                        fontWeight: 400,
                        fontSize: isMobile ? '1rem' : '1.1rem',
                        color: '#666',
                        textAlign: 'center',
                        margin: 0,
                        lineHeight: 1.5
                    }}>
                        Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                </div>

                {/* Privacy Policy Content */}
                <div style={{
                    width: '100%',
                    maxWidth: 800,
                    fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                    fontSize: isMobile ? '0.95rem' : '1.05rem',
                    lineHeight: 1.6,
                    color: '#444'
                }}>
                    {/* Introduction */}
                    <section style={{ marginBottom: isMobile ? 32 : 40 }}>
                        <p style={{ margin: '0 0 16px 0' }}>
                            This Privacy Policy describes how Carrie.ai ("we," "our," or "us") collects, uses, and protects your personal information when you use the Carrie.ai PowerPoint add-in (the "Service"). This policy applies specifically to the Carrie.ai add-in and our related services, not to our website or other unrelated services.
                        </p>
                    </section>

                    {/* Information We Collect */}
                    <section style={{ marginBottom: isMobile ? 32 : 40 }}>
                        <h2 style={{
                            fontWeight: 600,
                            fontSize: isMobile ? '1.3rem' : '1.5rem',
                            color: '#181c2a',
                            margin: '0 0 16px 0'
                        }}>
                            Information We Collect
                        </h2>
                        
                        <h3 style={{
                            fontWeight: 600,
                            fontSize: isMobile ? '1.1rem' : '1.2rem',
                            color: '#333',
                            margin: '24px 0 12px 0'
                        }}>
                            Information You Provide
                        </h3>
                        <ul style={{ paddingLeft: 20, margin: '0 0 16px 0' }}>
                            <li style={{ marginBottom: 8 }}>Text content and presentation data you input into the Carrie.ai add-in</li>
                            <li style={{ marginBottom: 8 }}>Commands and requests you make to our AI assistant</li>
                            <li style={{ marginBottom: 8 }}>Feedback and communications you send to us</li>
                        </ul>

                        <h3 style={{
                            fontWeight: 600,
                            fontSize: isMobile ? '1.1rem' : '1.2rem',
                            color: '#333',
                            margin: '24px 0 12px 0'
                        }}>
                            Automatically Collected Information
                        </h3>
                        <ul style={{ paddingLeft: 20, margin: '0 0 16px 0' }}>
                            <li style={{ marginBottom: 8 }}>Usage data and interaction patterns with the add-in</li>
                            <li style={{ marginBottom: 8 }}>Technical information about your device and PowerPoint version</li>
                            <li style={{ marginBottom: 8 }}>Error logs and performance metrics to improve our service</li>
                        </ul>
                    </section>

                    {/* How We Use Your Information */}
                    <section style={{ marginBottom: isMobile ? 32 : 40 }}>
                        <h2 style={{
                            fontWeight: 600,
                            fontSize: isMobile ? '1.3rem' : '1.5rem',
                            color: '#181c2a',
                            margin: '0 0 16px 0'
                        }}>
                            How We Use Your Information
                        </h2>
                        <p style={{ margin: '0 0 16px 0' }}>
                            We use your personal information to:
                        </p>
                        <ul style={{ paddingLeft: 20, margin: '0 0 16px 0' }}>
                            <li style={{ marginBottom: 8 }}>Provide and improve the Carrie.ai PowerPoint add-in functionality</li>
                            <li style={{ marginBottom: 8 }}>Process your AI-powered presentation editing requests</li>
                            <li style={{ marginBottom: 8 }}>Analyze usage patterns to enhance user experience</li>
                            <li style={{ marginBottom: 8 }}>Provide customer support and respond to your inquiries</li>
                            <li style={{ marginBottom: 8 }}>Detect and prevent technical issues or security threats</li>
                            <li style={{ marginBottom: 8 }}>Comply with legal obligations and protect our rights</li>
                        </ul>
                    </section>

                    {/* Information Sharing */}
                    <section style={{ marginBottom: isMobile ? 32 : 40 }}>
                        <h2 style={{
                            fontWeight: 600,
                            fontSize: isMobile ? '1.3rem' : '1.5rem',
                            color: '#181c2a',
                            margin: '0 0 16px 0'
                        }}>
                            Information Sharing and Disclosure
                        </h2>
                        <p style={{ margin: '0 0 16px 0' }}>
                            We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                        </p>
                        <ul style={{ paddingLeft: 20, margin: '0 0 16px 0' }}>
                            <li style={{ marginBottom: 8 }}>With AI service providers (such as OpenAI) to process your requests - these providers are bound by strict confidentiality agreements</li>
                            <li style={{ marginBottom: 8 }}>With cloud infrastructure providers (such as Microsoft Azure) to host and operate our service</li>
                            <li style={{ marginBottom: 8 }}>When required by law or to protect our legal rights</li>
                            <li style={{ marginBottom: 8 }}>In connection with a business transfer or acquisition (with prior notice to users)</li>
                        </ul>
                    </section>

                    {/* Data Security */}
                    <section style={{ marginBottom: isMobile ? 32 : 40 }}>
                        <h2 style={{
                            fontWeight: 600,
                            fontSize: isMobile ? '1.3rem' : '1.5rem',
                            color: '#181c2a',
                            margin: '0 0 16px 0'
                        }}>
                            Data Security
                        </h2>
                        <p style={{ margin: '0 0 16px 0' }}>
                            We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                        </p>
                        <ul style={{ paddingLeft: 20, margin: '0 0 16px 0' }}>
                            <li style={{ marginBottom: 8 }}>Encryption of data in transit and at rest</li>
                            <li style={{ marginBottom: 8 }}>Regular security assessments and updates</li>
                            <li style={{ marginBottom: 8 }}>Limited access to personal information on a need-to-know basis</li>
                            <li style={{ marginBottom: 8 }}>Secure cloud infrastructure with enterprise-grade security controls</li>
                        </ul>
                    </section>

                    {/* Data Retention */}
                    <section style={{ marginBottom: isMobile ? 32 : 40 }}>
                        <h2 style={{
                            fontWeight: 600,
                            fontSize: isMobile ? '1.3rem' : '1.5rem',
                            color: '#181c2a',
                            margin: '0 0 16px 0'
                        }}>
                            Data Retention
                        </h2>
                        <p style={{ margin: '0 0 16px 0' }}>
                            We retain your personal information only for as long as necessary to provide our services and fulfill the purposes outlined in this Privacy Policy. Presentation content and AI interaction data are typically processed in real-time and not permanently stored unless necessary for service improvement or troubleshooting.
                        </p>
                    </section>

                    {/* Your Rights */}
                    <section style={{ marginBottom: isMobile ? 32 : 40 }}>
                        <h2 style={{
                            fontWeight: 600,
                            fontSize: isMobile ? '1.3rem' : '1.5rem',
                            color: '#181c2a',
                            margin: '0 0 16px 0'
                        }}>
                            Your Rights and Choices
                        </h2>
                        <p style={{ margin: '0 0 16px 0' }}>
                            Depending on your location, you may have certain rights regarding your personal information, including:
                        </p>
                        <ul style={{ paddingLeft: 20, margin: '0 0 16px 0' }}>
                            <li style={{ marginBottom: 8 }}>The right to access and receive a copy of your personal information</li>
                            <li style={{ marginBottom: 8 }}>The right to rectify inaccurate personal information</li>
                            <li style={{ marginBottom: 8 }}>The right to erase your personal information in certain circumstances</li>
                            <li style={{ marginBottom: 8 }}>The right to restrict or object to processing of your personal information</li>
                            <li style={{ marginBottom: 8 }}>The right to data portability</li>
                        </ul>
                        <p style={{ margin: '0 0 16px 0' }}>
                            To exercise these rights, please contact us at <a href="mailto:wafi@utexas.edu" style={{ color: '#7f6fff', textDecoration: 'underline' }}>wafi@utexas.edu</a>.
                        </p>
                    </section>

                    {/* Third-Party Services */}
                    <section style={{ marginBottom: isMobile ? 32 : 40 }}>
                        <h2 style={{
                            fontWeight: 600,
                            fontSize: isMobile ? '1.3rem' : '1.5rem',
                            color: '#181c2a',
                            margin: '0 0 16px 0'
                        }}>
                            Third-Party Services
                        </h2>
                        <p style={{ margin: '0 0 16px 0' }}>
                            The Carrie.ai add-in integrates with Microsoft PowerPoint and may use third-party AI services to provide functionality. These third-party services have their own privacy policies, and we encourage you to review them. We are not responsible for the privacy practices of these third-party services.
                        </p>
                    </section>

                    {/* Children's Privacy */}
                    <section style={{ marginBottom: isMobile ? 32 : 40 }}>
                        <h2 style={{
                            fontWeight: 600,
                            fontSize: isMobile ? '1.3rem' : '1.5rem',
                            color: '#181c2a',
                            margin: '0 0 16px 0'
                        }}>
                            Children's Privacy
                        </h2>
                        <p style={{ margin: '0 0 16px 0' }}>
                            Our Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
                        </p>
                    </section>

                    {/* International Data Transfers */}
                    <section style={{ marginBottom: isMobile ? 32 : 40 }}>
                        <h2 style={{
                            fontWeight: 600,
                            fontSize: isMobile ? '1.3rem' : '1.5rem',
                            color: '#181c2a',
                            margin: '0 0 16px 0'
                        }}>
                            International Data Transfers
                        </h2>
                        <p style={{ margin: '0 0 16px 0' }}>
                            Your personal information may be transferred to and processed in countries other than your country of residence. We ensure that such transfers are conducted in accordance with applicable data protection laws and with appropriate safeguards in place.
                        </p>
                    </section>

                    {/* Changes to Privacy Policy */}
                    <section style={{ marginBottom: isMobile ? 32 : 40 }}>
                        <h2 style={{
                            fontWeight: 600,
                            fontSize: isMobile ? '1.3rem' : '1.5rem',
                            color: '#181c2a',
                            margin: '0 0 16px 0'
                        }}>
                            Changes to This Privacy Policy
                        </h2>
                        <p style={{ margin: '0 0 16px 0' }}>
                            We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated Privacy Policy on this page and updating the "Last updated" date above.
                        </p>
                    </section>

                    {/* Contact Information */}
                    <section style={{ marginBottom: isMobile ? 32 : 40 }}>
                        <h2 style={{
                            fontWeight: 600,
                            fontSize: isMobile ? '1.3rem' : '1.5rem',
                            color: '#181c2a',
                            margin: '0 0 16px 0'
                        }}>
                            Contact Us
                        </h2>
                        <p style={{ margin: '0 0 16px 0' }}>
                            If you have any questions about this Privacy Policy or our privacy practices regarding the Carrie.ai PowerPoint add-in, please contact us at:
                        </p>
                        <div style={{
                            background: '#f8f9fa',
                            padding: isMobile ? 16 : 20,
                            borderRadius: 8,
                            border: '1px solid #e9ecef',
                            margin: '16px 0'
                        }}>
                            <p style={{ margin: '0 0 8px 0', fontWeight: 500 }}>Carrie.ai</p>
                            <p style={{ margin: '0 0 8px 0' }}>Email: <a href="mailto:wafi@utexas.edu" style={{ color: '#7f6fff', textDecoration: 'underline' }}>wafi@utexas.edu</a></p>
                            <p style={{ margin: '0 0 8px 0' }}>Phone: <a href="tel:+14695446042" style={{ color: '#7f6fff', textDecoration: 'underline' }}>(469) 544-6042</a></p>
                            <p style={{ margin: 0 }}>Twitter: <a href="https://twitter.com/wafitoffy_" target="_blank" rel="noopener noreferrer" style={{ color: '#7f6fff', textDecoration: 'underline' }}>@wafitoffy_</a></p>
                        </div>
                    </section>

                    {/* Service Description */}
                    <section style={{ 
                        marginBottom: isMobile ? 32 : 40,
                        background: 'rgba(127, 111, 255, 0.05)',
                        padding: isMobile ? 16 : 20,
                        borderRadius: 8,
                        border: '1px solid rgba(127, 111, 255, 0.1)'
                    }}>
                        <h2 style={{
                            fontWeight: 600,
                            fontSize: isMobile ? '1.3rem' : '1.5rem',
                            color: '#181c2a',
                            margin: '0 0 16px 0'
                        }}>
                            About Carrie.ai
                        </h2>
                        <p style={{ margin: 0 }}>
                            Carrie.ai is an AI-powered PowerPoint add-in that serves as your intelligent presentation assistant. The add-in enables conversational editing, smart data updates, intelligent layouts, and manager feedback integration to enhance your PowerPoint experience. This privacy policy specifically governs the collection and use of personal information through the Carrie.ai PowerPoint add-in service.
                        </p>
                    </section>
                </div>
            </main>

            {/* Footer */}
            <footer style={{
                width: '100%',
                padding: isMobile ? '32px 0 24px 0' : '48px 0 32px 0',
                textAlign: 'center',
                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                fontWeight: 400,
                fontSize: isMobile ? '0.85rem' : '0.95rem',
                color: '#888',
                borderTop: '1px solid #eee',
                marginTop: 40
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: isMobile ? 16 : 32,
                    marginBottom: 16
                }}>
                    <a href="/" style={{ color: '#888', textDecoration: 'none' }}>Home</a>
                    <a href="/demo" style={{ color: '#888', textDecoration: 'none' }}>Install</a>
                    <a href="/privacy" style={{ color: '#7f6fff', textDecoration: 'none' }}>Privacy Policy</a>
                </div>
                <p style={{ margin: 0 }}>
                    © {new Date().getFullYear()} Carrie.ai. All rights reserved.
                </p>
            </footer>
        </div>
    );
}

export default PrivacyPolicyPage; 